import { Field, ErrorMessage, useFormikContext } from 'formik';
import InputMask from 'react-input-mask';
import RegionalInstitutionPicker from './RegionalInstitutionPicker';
import { answerKey } from '../validationSchema';

/* ─── inline error message ──────────────────────────────────────────────────── */
function FieldError({ name }) {
  return (
    <ErrorMessage
      name={name}
      render={msg => (
        <p className="mt-1.5 flex items-start gap-1.5 text-sm text-red-600">
          <svg
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 flex-none"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <circle
              cx="12"
              cy="12"
              r="10"
            />
            <path
              strokeLinecap="round"
              d="M12 8v4M12 16h.01"
            />
          </svg>
          <span>{msg}</span>
        </p>
      )}
    />
  );
}

/* ─── input className helper ────────────────────────────────────────────────── */
function fieldCx(touched, error) {
  const hasError = touched && error;
  return [
    'mt-1.5 block w-full rounded-lg border bg-white px-3.5 py-2.5 text-base text-slate-900 shadow-sm ring-1 ring-inset transition',
    'placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset',
    hasError ? 'border-red-300 ring-red-200' : 'border-slate-200 ring-slate-200'
  ].join(' ');
}

/* ─── section header ────────────────────────────────────────────────────────── */
function SectionHeader({ number, title, description }) {
  return (
    <header className="mb-5">
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700">{number}</span>
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      </div>
      {description && <p className="ml-11 mt-1 text-sm text-slate-500">{description}</p>}
    </header>
  );
}

function Req() {
  return (
    <span
      className="text-red-500"
      aria-hidden="true"
    >
      {' '}
      *
    </span>
  );
}

function Label({ children }) {
  return <span className="block text-sm font-medium text-slate-700">{children}</span>;
}

/* ─── main component ────────────────────────────────────────────────────────── */
export default function RegistrationFormFields({ slug, values, setFieldValue, questions = [], targetAudience = 'student', collectEducationInfo = true, collectWorkInfo = true }) {
  const { touched, errors } = useFormikContext();

  const showWorkplaceSection = targetAudience === 'student' || collectWorkInfo;
  const workplaceTitle = targetAudience === 'teacher' ? 'Onde você trabalha' : 'Onde você quer estudar';
  const workplaceDescription = targetAudience === 'teacher' ? 'Selecione a regional e a unidade de ensino onde você atua.' : 'Selecione a regional e a unidade de ensino de interesse.';

  return (
    <>
      {/* ── Section 1: Seus dados ── */}
      <section className="px-6 pb-8 pt-8 sm:px-8">
        <SectionHeader
          number="1"
          title="Seus dados"
          description="Informações para identificação e contato."
        />
        <div className="space-y-5">
          <label className="block">
            <Label>
              Nome completo
              <Req />
            </Label>
            <Field
              name="full_name"
              autoComplete="name"
              className={fieldCx(touched.full_name, errors.full_name)}
            />
            <FieldError name="full_name" />
          </label>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block">
              <Label>
                CPF
                <Req />
              </Label>
              <Field name="cpf">
                {({ field }) => (
                  <InputMask
                    {...field}
                    mask="999.999.999-99"
                    maskChar={null}
                    placeholder="000.000.000-00"
                    autoComplete="off"
                    inputMode="numeric"
                    className={fieldCx(touched.cpf, errors.cpf)}
                  />
                )}
              </Field>
              <FieldError name="cpf" />
            </label>
            <label className="block">
              <Label>
                Data de nascimento
                <Req />
              </Label>
              <Field
                name="birth_date"
                type="date"
                className={fieldCx(touched.birth_date, errors.birth_date)}
              />
              <FieldError name="birth_date" />
            </label>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block">
              <Label>
                Email
                <Req />
              </Label>
              <Field
                name="email"
                type="email"
                autoComplete="email"
                className={fieldCx(touched.email, errors.email)}
              />
              <FieldError name="email" />
            </label>
            <label className="block">
              <Label>
                Telefone / WhatsApp
                <Req />
              </Label>
              <Field name="phone">
                {({ field }) => (
                  <InputMask
                    {...field}
                    mask="(99) 99999-9999"
                    maskChar={null}
                    placeholder="(11) 99999-9999"
                    autoComplete="tel"
                    type="tel"
                    inputMode="tel"
                    className={fieldCx(touched.phone, errors.phone)}
                  />
                )}
              </Field>
              <FieldError name="phone" />
            </label>
          </div>
        </div>
      </section>

      {/* ── Section 2: Onde você quer estudar / trabalha (opcional para professor) ── */}
      {showWorkplaceSection && (
        <section className="border-t border-slate-200 px-6 py-8 sm:px-8">
          <SectionHeader
            number="2"
            title={workplaceTitle}
            description={workplaceDescription}
          />
          <RegionalInstitutionPicker
            slug={slug}
            regionalId={values.regional_id}
            institutionId={values.institution_id}
            onChange={patch => Object.entries(patch).forEach(([k, v]) => setFieldValue(k, v))}
          />
          <FieldError name="regional_id" />
          <FieldError name="institution_id" />
        </section>
      )}

      {/* ── Section 3: Sua escolaridade (student only, opcional) ── */}
      {targetAudience === 'student' && collectEducationInfo && (
        <section className="border-t border-slate-200 px-6 py-8 sm:px-8">
          <SectionHeader
            number="3"
            title="Sua escolaridade"
            description="Informe sua situação escolar atual e o que está buscando."
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <label className="block">
              <Label>
                Escolaridade atual
                <Req />
              </Label>
              <Field
                as="select"
                name="current_education_level"
                className={fieldCx(touched.current_education_level, errors.current_education_level)}
              >
                <option value="">Selecione...</option>
                <option value="fundamental_2">Ensino Fundamental II</option>
                <option value="medio_1">1º Ano EM</option>
                <option value="medio_2">2º Ano EM</option>
                <option value="medio_3">3º Ano EM</option>
                <option value="concluido">EM Concluído</option>
                <option value="outro">Outro</option>
              </Field>
              <FieldError name="current_education_level" />
            </label>
            <label className="block">
              <Label>
                Série pretendida
                <Req />
              </Label>
              <Field
                as="select"
                name="desired_grade"
                className={fieldCx(touched.desired_grade, errors.desired_grade)}
              >
                <option value="">Selecione...</option>
                <option value="1º Ano EM">1º Ano EM</option>
                <option value="2º Ano EM">2º Ano EM</option>
                <option value="3º Ano EM">3º Ano EM</option>
                <option value="Outro">Outro</option>
              </Field>
              <FieldError name="desired_grade" />
            </label>
            <label className="block">
              <Label>
                Turno
                <Req />
              </Label>
              <Field
                as="select"
                name="preferred_shift"
                className={fieldCx(touched.preferred_shift, errors.preferred_shift)}
              >
                <option value="">Selecione...</option>
                <option value="morning">Manhã</option>
                <option value="afternoon">Tarde</option>
                <option value="evening">Noite</option>
              </Field>
              <FieldError name="preferred_shift" />
            </label>
          </div>
        </section>
      )}

      {/* ── Section 4: Outras informações ── */}
      <section className="border-t border-slate-200 px-6 py-8 sm:px-8">
        <SectionHeader
          number="4"
          title="Outras informações"
          description="Informe caso necessite de apoio ou recursos especiais."
        />
        <label className="flex cursor-pointer items-start gap-3">
          <Field
            type="checkbox"
            name="special_needs"
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-slate-700">Tenho alguma necessidade especial ou de acessibilidade</span>
        </label>
        {values.special_needs && (
          <label className="mt-4 block">
            <Label>Descreva as necessidades</Label>
            <Field
              as="textarea"
              name="special_needs_description"
              rows={3}
              placeholder="Descreva com detalhes as adaptações necessárias..."
              className={`${fieldCx(touched.special_needs_description, errors.special_needs_description)} resize-none`}
            />
            <FieldError name="special_needs_description" />
          </label>
        )}
      </section>

      {/* ── Section 5: Perguntas adicionais ── */}
      {questions.length > 0 && (
        <section className="border-t border-slate-200 px-6 py-8 sm:px-8">
          <SectionHeader
            number="5"
            title="Perguntas adicionais"
            description="Responda as perguntas configuradas para este seletivo."
          />
          <div className="space-y-5">
            {questions.map(q => {
              const key = answerKey(q);
              const fieldName = `answers.${key}`;
              const touchedQ = touched?.answers?.[key];
              const errorQ = errors?.answers?.[key];

              return (
                <div key={q.id}>
                  <Label>
                    {q.label}
                    {q.required && <Req />}
                  </Label>

                  {q.kind === 'short_text' && (
                    <Field
                      type="text"
                      name={fieldName}
                      className={fieldCx(touchedQ, errorQ)}
                    />
                  )}

                  {q.kind === 'long_text' && (
                    <Field
                      as="textarea"
                      name={fieldName}
                      rows={4}
                      className={`${fieldCx(touchedQ, errorQ)} resize-none`}
                    />
                  )}

                  {q.kind === 'select' && (
                    <Field
                      as="select"
                      name={fieldName}
                      className={fieldCx(touchedQ, errorQ)}
                    >
                      <option value="">Selecione...</option>
                      {(q.options || []).map(opt => (
                        <option
                          key={opt}
                          value={opt}
                        >
                          {opt}
                        </option>
                      ))}
                    </Field>
                  )}

                  {q.kind === 'radio' && (
                    <div className="mt-2 flex flex-col gap-2">
                      {(q.options || []).map(opt => (
                        <label
                          key={opt}
                          className="flex cursor-pointer items-center gap-2"
                        >
                          <Field
                            type="radio"
                            name={fieldName}
                            value={opt}
                            className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-slate-700">{opt}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {q.kind === 'file' && (
                    <div>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={e => {
                          const file = e.target.files?.[0] || null;
                          setFieldValue(fieldName, file);
                        }}
                        className="mt-1.5 block w-full text-sm text-slate-600 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-blue-700 hover:file:bg-blue-100"
                      />
                      {values?.answers?.[key] instanceof File && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-slate-600">
                          <span className="truncate">{values.answers[key].name}</span>
                          <button
                            type="button"
                            onClick={() => setFieldValue(fieldName, null)}
                            className="font-semibold text-blue-600 hover:underline"
                          >
                            Trocar
                          </button>
                        </div>
                      )}
                      <p className="mt-1 text-xs text-slate-500">PDF, JPG ou PNG, até 5 MB.</p>
                    </div>
                  )}

                  {q.kind === 'checkbox' && (
                    <div className="mt-2 flex flex-col gap-2">
                      {(q.options || []).map(opt => {
                        const arr = Array.isArray(values?.answers?.[key]) ? values.answers[key] : [];
                        const checked = arr.includes(opt);
                        return (
                          <label
                            key={opt}
                            className="flex cursor-pointer items-center gap-2"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={e => {
                                const next = e.target.checked ? [...arr, opt] : arr.filter(v => v !== opt);
                                setFieldValue(fieldName, next);
                              }}
                              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-slate-700">{opt}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  <FieldError name={fieldName} />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── honeypot (fully hidden from screen readers and keyboard) ── */}
      <div
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
      >
        <Field
          type="text"
          name="honeypot"
          tabIndex={-1}
          autoComplete="off"
        />
      </div>
    </>
  );
}
